import { useCallback, useEffect, useState } from 'react';

/**
 * 「쉬운 모드」 — 처음 쓰는 사람과 눈이 편치 않은 사람을 위한 화면 (DOC-31).
 *
 * <h2>무엇이 바뀌나</h2>
 * <ul>
 *   <li><b>다음 할 일 띠</b>가 화면 위에 뜬다 — 지금 무엇을 눌러야 하는지 한 문장
 *   <li><b>한 화면에 한 가지</b> — 검측 카드가 4단계로 쪼개진다
 *   <li><b>큰 글씨·큰 버튼</b> — 장갑 낀 손가락과 노안을 함께 감안한다
 * </ul>
 *
 * <h2>왜 기기에 저장하나</h2>
 * 안내 진행 기록({@code useTourProgress})은 서버에 둔다 — 기기를 옮겨도 이미 본 안내가 또 뜨면 안
 * 되기 때문이다. 쉬운 모드는 반대다. <b>같은 사람이라도 기기마다 답이 다르다</b> —
 * 현장에서 장갑 끼고 쓰는 휴대폰에서는 켜고, 사무실 큰 모니터에서는 끈다.
 * 그래서 계정이 아니라 <b>이 기기</b>에 남긴다.
 *
 * <h2>기본은 꺼짐이다</h2>
 * 처음에는 「설정을 찾아 켤 줄 모르는 사람」 을 위해 켜진 채로 시작했다. 그런데 화면의 버튼을
 * 없앤 뒤(2026-09-23)로는 켜진 것을 되돌릴 곳이 화면에 없어, 처음 들어온 기기마다 큰 글씨가
 * 이유 없이 켜져 보였다(사용자 지적 2026-09-29). 그래서 저장된 값이 없으면 꺼진 채로 시작하고,
 * 켜는 일은 AI Agent 에게 「글씨 크게 해줘」 라고 말하는 것으로 한다.
 */

const STORAGE_KEY = 'bomi.easyMode';

/** 켜짐/꺼짐이 바뀌었음을 같은 탭의 다른 컴포넌트에 알린다(storage 이벤트는 다른 탭에만 간다). */
const CHANGE_EVENT = 'bomi:easy-mode';

function read(): boolean {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    // 저장된 적이 없으면 꺼진 채로 시작한다(위 주석). 켠 사람만 '1' 이 남아 켜져 있다.
    return saved === '1';
  } catch {
    return false;
  }
}

/**
 * 훅 <b>밖에서</b> 켜고 끈다 — AI 대화가 쓰는 손(`actions.ts` 의 {@code set_easy_mode}).
 *
 * <p>화면의 버튼을 없앤 뒤(2026-09-23) 켜고 끄는 길이 대화 하나가 됐는데, 그쪽은 카탈로그의
 * 순수 함수라 훅을 부를 수 없다. 저장·알림은 {@link useEasyMode} 의 {@code set} 과 <b>같은 줄</b>을
 * 탄다 — 두 벌로 적으면 한쪽만 이벤트를 쏘는 날 다른 탭·다른 컴포넌트가 옛 값을 들고 남는다.
 */
export function setEasyMode(next: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    // 저장을 못 해도 이번 세션 동안은 유지된다. 되돌릴 것이 없다.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** 지금 켜져 있는가 — 훅 밖에서 묻는다. 대화가 「지금 상태」를 말할 때 쓴다. */
export function easyModeOn(): boolean {
  return read();
}

export function useEasyMode(): { easy: boolean; toggle: () => void; set: (next: boolean) => void } {
  const [easy, setEasy] = useState(read);

  useEffect(() => {
    const sync = () => setEasy(read());
    window.addEventListener(CHANGE_EVENT, sync);
    // 다른 탭에서 바꾼 것도 따라간다 — 한 사람이 두 탭을 열어 두는 일은 흔하다.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const set = useCallback((next: boolean) => {
    // 저장·알림은 모듈 함수 한 줄을 탄다. `sync` 가 그 알림을 받아 `easy` 를 갱신하지만,
    // 누른 사람의 화면이 한 프레임이라도 옛 값을 그리지 않도록 여기서도 바로 넣는다.
    setEasyMode(next);
    setEasy(next);
  }, []);

  const toggle = useCallback(() => set(!read()), [set]);

  return { easy, toggle, set };
}

/**
 * 본문 전체에 거는 클래스. 큰 글씨·큰 버튼은 CSS 한 곳(`index.css` 의 {@code .easy-mode})에 모은다 —
 * 컴포넌트마다 조건부 스타일을 쓰면 어디가 커지는지 아무도 추적하지 못한다.
 */
export const EASY_MODE_CLASS = 'easy-mode';
